import React, { useState, useRef, useCallback } from 'react';
import Title from './Title';
import Bubbles from './Bubbles';

const ventures = [
  {
    id: 1,
    name: 'WeTech',
    role: 'CTO',
    description: 'Full-service digital agency delivering end-to-end web solutions, SaaS platforms, and enterprise applications. Leading the entire technical vision, architecture decisions, and engineering operations across all client engagements and internal products.',
    highlights: [
      'Architecting scalable microservices and cloud infrastructure',
      'Leading a multidisciplinary engineering team',
      'Driving technical standards, code quality, and deployment pipelines',
      'Building reusable frameworks and internal tooling',
    ],
    tags: ['React', 'Node.js', 'Laravel', 'Cloud', 'DevOps'],
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
      </svg>
    ),
  },
  {
    id: 2,
    name: 'WeGrow',
    role: 'Ecosystem Platform',
    description: 'A collaborative growth ecosystem designed to help businesses scale through interconnected services, strategic partnerships, and shared digital infrastructure. WeGrow acts as the connective tissue between ventures, enabling resource sharing, cross-platform integrations, and unified analytics.',
    highlights: [
      'Unified dashboard for managing multiple business verticals',
      'API-first architecture enabling seamless third-party integrations',
      'Partner onboarding and ecosystem analytics modules',
      'Scalable SaaS backbone for multi-tenant service delivery',
    ],
    tags: ['Platform', 'SaaS', 'API', 'Analytics', 'Integration'],
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
      </svg>
    ),
  },
  {
    id: 3,
    name: 'A4E',
    role: 'Strategic Venture',
    description: 'A forward-looking strategic venture focused on identifying market gaps and building innovative digital products from concept to launch. A4E operates as the innovation lab within the ecosystem, prototyping new ideas, validating market fit, and rapidly iterating on product solutions.',
    highlights: [
      'Rapid prototyping and MVP development pipeline',
      'Market research and product-market fit validation',
      'Cross-venture technology transfer and knowledge sharing',
      'Investor-ready product presentations and pitch support',
    ],
    tags: ['Innovation', 'Strategy', 'MVP', 'Product-Led'],
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
  },
  {
    id: 4,
    name: 'NEX CARD',
    role: 'Digital Product',
    description: 'A premium NFC-enabled digital business card engineered and launched under direct leadership. NEX CARD replaces traditional paper cards with a sleek, tap-to-share experience that instantly transfers your professional profile, portfolio, and contact details to any smartphone.',
    highlights: [
      'Custom NFC chip firmware and proximity-based data transfer',
      'Companion mobile app for profile management and analytics',
      'Real-time card tap analytics and lead capture dashboard',
      'Premium materials with customizable engraving and branding',
    ],
    tags: ['NFC', 'Hardware', 'Mobile', 'IoT', 'Product'],
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
      </svg>
    ),
  },
];

const VentureCard = ({ venture, index }) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    setTilt({
      x: ((y - centerY) / centerY) * -8,
      y: ((x - centerX) / centerX) * 8,
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
    setIsHovered(false);
  }, []);

  return (
    <article
      ref={cardRef}
      className="group relative p-6 lg:p-8 bg-primary-card rounded-2xl border border-primary-border overflow-hidden card-shine"
      data-aos="fade-up"
      data-aos-duration="700"
      data-aos-delay={index * 120}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        transition: 'transform 0.15s ease-out',
      }}
    >
      {/* Gradient overlay on hover */}
      <div className={`absolute inset-0 bg-gradient-to-br from-accent/8 via-transparent to-accent/5 transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} />

      {/* Animated border glow */}
      <div className={`absolute inset-0 rounded-2xl transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        style={{
          boxShadow: 'inset 0 0 30px rgba(201, 169, 110, 0.05), 0 0 30px rgba(201, 169, 110, 0.05)',
        }}
      />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className={`p-3 rounded-xl transition-all duration-500 flex-shrink-0 ${
            isHovered
              ? 'bg-accent text-primary-bg shadow-accent-lg scale-110 rotate-3'
              : 'bg-accent/10 text-accent'
          }`}>
            {venture.icon}
          </div>
          <span className="text-[11px] text-accent bg-accent/10 px-3 py-1 rounded-full font-medium whitespace-nowrap border border-accent/20">
            {venture.role}
          </span>
        </div>

        <h3 className={`text-xl lg:text-2xl font-bold mb-3 transition-colors duration-300 ${
          isHovered ? 'text-accent' : 'text-white'
        }`}>
          {venture.name}
        </h3>

        <p className="text-gray leading-relaxed mb-4 text-sm lg:text-base">
          {venture.description}
        </p>

        <ul className="space-y-1.5 mb-5">
          {venture.highlights.map((item, i) => (
            <li key={i} className="flex items-start text-sm text-gray-light">
              <span className={`mr-2 mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                isHovered ? 'bg-accent shadow-accent scale-125' : 'bg-accent/60'
              }`} />
              {item}
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-2">
          {venture.tags.map((tag, i) => (
            <span
              key={i}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-all duration-300 ${
                isHovered
                  ? 'text-accent border-accent/40 bg-accent/10'
                  : 'text-gray-dark border-primary-border bg-primary-bg'
              }`}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
};

const ExecutiveExperience = () => {
  return (
    <section id="ventures" className="relative bg-primary-dark py-24 overflow-hidden noise-overlay">
      <div className="absolute inset-0 gradient-mesh pointer-events-none" />
      <Bubbles count={7} />

      {/* Decorative elements */}
      <div className="absolute top-10 left-10 w-40 h-40 border border-accent/5 rounded-full animate-spin-slow pointer-events-none hidden sm:block" />
      <div className="absolute bottom-10 right-10 w-24 h-24 border border-accent/5 rounded-full animate-spin-slow pointer-events-none hidden sm:block" style={{ animationDirection: 'reverse' }} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Title>Featured Ventures & Products</Title>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6 mt-12">
          {ventures.map((venture, index) => (
            <VentureCard key={venture.id} venture={venture} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExecutiveExperience;
